import { useState } from "react";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { useFrontendSetting } from "@nens/ranalib";
import { useLocalStorage, useMapListener } from "@nens/react/hooks";
import { getMaptilerStyleJsonUrl } from "@nens/react-map-gl/utils";
import { NETHERLANDS_EXTENT, objectKeys, satisfiesRecord } from "@nens/shared-utils";

// The map center may be this many degrees outside the Netherlands extent before BRT is disabled.
const BRT_BOUNDARY_MARGIN_DEGREES = 0.2;

type MapBounds = {
  getWest: () => number;
  getSouth: () => number;
  getEast: () => number;
  getNorth: () => number;
};

type Background = {
  title: string;
  description: string;
  type: "maptiler" | "pdok";
  disabledTitle?: string;
};

const isMapCenterOutsideBrtBoundary = (bounds: MapBounds) => {
  // NETHERLANDS_EXTENT is [west, south, east, north]. Expand each side by the BRT margin
  // and compare it with the map center, so zooming out does not disable BRT by itself.
  const [west, south, east, north] = NETHERLANDS_EXTENT;
  const mapCenterLongitude = (bounds.getWest() + bounds.getEast()) / 2;
  const mapCenterLatitude = (bounds.getSouth() + bounds.getNorth()) / 2;

  return (
    mapCenterLatitude > north + BRT_BOUNDARY_MARGIN_DEGREES ||
    mapCenterLatitude < south - BRT_BOUNDARY_MARGIN_DEGREES ||
    mapCenterLongitude > east + BRT_BOUNDARY_MARGIN_DEGREES ||
    mapCenterLongitude < west - BRT_BOUNDARY_MARGIN_DEGREES
  );
};

export const useRanaMapBackground = (mapId: string) => {
  const intl = useIntl();
  const { [mapId]: map } = useMap();
  // If view is outside the Netherlands the BRT background map should be disabled, because it is only available for the Netherlands. This state is updated on every moveend event.
  const [viewIsOutsideOfTheNetherlands, setviewIsOutsideOfTheNetherlands] = useState(false);
  const updateIsOutsideOfTheNetherlands = () => {
    const bounds = map?.getBounds();
    if (bounds) {
      const isOutsideOfTheNetherlands = isMapCenterOutsideBrtBoundary(bounds);
      setviewIsOutsideOfTheNetherlands(isOutsideOfTheNetherlands);
    }
  };
  useMapListener("moveend", updateIsOutsideOfTheNetherlands, map);

  const backgrounds = satisfiesRecord<Background>()({
    topo: {
      title: "Topo",
      description: "Default background map",
      type: "maptiler",
    },
    "979c501d-dd64-478a-a7bf-960515d5c0d2": {
      title: "Satellite",
      description: "Satellite background map",
      type: "maptiler",
    },
    "d481b712-934d-485d-bfdb-bc1abf01fd5c": {
      title: "Grey",
      description: "Grey background map",
      type: "maptiler",
    },
    standaard__webmercatorquad: {
      title: "BRT",
      description: "NL topographic map, powered by PDOK (BRT)",
      type: "pdok",
      disabledTitle: viewIsOutsideOfTheNetherlands
        ? intl.formatMessage({
            id: "background.brt.disabledTitle",
            defaultMessage:
              "BRT is disabled because the map is outside of the Netherlands, will use Topo instead",
          })
        : undefined,
    },
  });

  type BackgroundKey = keyof typeof backgrounds;
  const backgroundKeys = objectKeys(backgrounds);
  const defaultKey: BackgroundKey = "topo";

  const [_backgroundMapKey, setBackgroundMapKey] = useLocalStorage<BackgroundKey>(
    "backgroundMapKey",
    defaultKey
  );
  // Validate the background map key to ensure it is one of the allowed keys. If not, reset to default.
  const backgroundMapKey = backgroundKeys.includes(_backgroundMapKey)
    ? _backgroundMapKey
    : defaultKey;

  const { maptilerApiKey } = useFrontendSetting();

  const background = backgrounds[backgroundMapKey];

  const backgroundTypeToUrlMap: { [K in Background["type"]]: string | null } = {
    maptiler: getMaptilerStyleJsonUrl(backgroundMapKey, maptilerApiKey),
    pdok: `https://api.pdok.nl/kadaster/brt-achtergrondkaart/ogc/v1/styles/${backgroundMapKey}?f=mapbox`,
  };

  let url = backgroundTypeToUrlMap[background.type];
  if (background.disabledTitle) {
    // For now this is only used for the BRT background map, which is disabled when the view is outside of the Netherlands. In that case we fall back to the default background map (topo).
    url = getMaptilerStyleJsonUrl(defaultKey, maptilerApiKey);
  }

  return {
    backgrounds,
    backgroundMapKey,
    setBackgroundMapKey,
    url,
  };
};
